import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ArrowLeftRight, Check, CreditCard, Eye, Info, Plus, Wallet } from 'lucide-react';
import {
  type CreatePaymentProviderRequest,
  isPaymentProviderValue,
  type ListPaymentProvidersResponse,
  PAYMENT_PROVIDER_VALUES,
  type PaymentProviderConfigRecord,
  type PaymentProviderValue,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreatePaymentProvider,
  usePaymentProviders,
  useRevealPaymentProviderKey,
} from '../api/payment-providers.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  Select,
} from '../ui/index.ts';

/**
 * `/payment-providers` — payment aggregator credential CRUD (PR P16).
 *
 * This page is the tenant-free counterpart to `/pricing` — it manages
 * the `payment_provider_configs` rows that `createZPayProvider` reads
 * at signing time. Every topup in the system depends on this surface;
 * a misconfigured row 503s `/v1/billing/topup` until ops fixes it.
 *
 * Layout — Dispatch Deck (epic #835):
 *
 *   1. PageHeader — title + subtitle + "New Provider" primary button.
 *   2. Info banner — "changes take effect within 60 seconds" (matches
 *      the isolate-local cache TTL in `db/payment-provider-configs.ts`).
 *   3. Provider list — one Panel per provider, rows grouped active-first.
 *      EmptyState card when the table is empty (fresh deploy, pre-seed).
 *   4. Reveal interaction — inline `[Reveal key]` button replaces the
 *      masked key with the plaintext one for the single row that was
 *      clicked. Every click hits the audited `/reveal-key` endpoint.
 *   5. Create / rotate modal — single form that writes a new row. The
 *      `Make active` toggle triggers the retire-flip on the prior
 *      active row atomically (platform-api batch).
 *   6. Type-to-confirm — whenever `Make active` is set AND a prior
 *      active row exists, the submit opens a second modal requiring
 *      the operator to type the provider name exactly. Matches the
 *      pricing + balance-adjust pattern.
 *
 * Append-only rotation — there is intentionally NO "edit in place"
 * button on existing rows. Rotating a provider means creating a new
 * row with new credentials and `make_active=true`; the DB retires the
 * prior active row in the same batch. The retired row's plaintext key
 * remains readable forever via reveal so auditors can verify historical
 * signatures.
 *
 * Plaintext-by-design — secret keys are stored plaintext in SQL (see
 * CLAUDE.md "Do NOT" #5 + migration 0016 rationale). The list view
 * only ever renders the `secret_key_masked` string; plaintext comes
 * from the `reveal-key` endpoint which writes an `ops_audit` row on
 * every call.
 */

export function PaymentProvidersPage(): JSX.Element {
  const t = useT();
  const list = usePaymentProviders();

  if (list.isPending) {
    return (
      <section
        className="flex flex-col gap-6"
        data-testid="payment-providers-page-loading"
      >
        <div className="h-24 animate-pulse rounded-lg border border-line-soft bg-card" aria-hidden />
        <div className="h-16 animate-pulse rounded-lg border border-line-soft bg-card" aria-hidden />
        <div className="h-64 animate-pulse rounded-lg border border-line-soft bg-card" aria-hidden />
        <span className="sr-only">{t('payment_providers.loading_aria')}</span>
      </section>
    );
  }

  if (list.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-lg border border-bad-line bg-bad-dim/40 p-6"
        role="alert"
        data-testid="payment-providers-page-error"
      >
        <h2 className="m-0 text-section text-bad-bright">
          {t('payment_providers.error.title')}
        </h2>
        <p className="m-0 text-body text-bad-bright">
          {list.error instanceof OpsApiError
            ? `${list.error.code}: ${list.error.message}`
            : t('common.network_error')}
        </p>
        <div>
          <Button variant="secondary" onClick={() => void list.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      </section>
    );
  }

  return <PaymentProvidersReady data={list.data} />;
}

interface ReadyProps {
  readonly data: ListPaymentProvidersResponse;
}

function PaymentProvidersReady({ data }: ReadyProps): JSX.Element {
  const t = useT();
  // `prefill` keeps the create-modal seeded with a specific provider
  // when the operator clicked "Rotate" on an existing row (vs the blank
  // "+ New Provider" button up top). When null → blank form.
  const [prefill, setPrefill] = useState<PaymentProviderValue | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  // Group rows by provider for display; each provider's active row
  // first, then retired rows newest-first. Server already sorts by
  // (provider ASC, updated_at DESC), we just need to hoist active.
  const rowsByProvider = useMemo(() => groupByProvider(data.providers), [data.providers]);

  function openCreateModal(provider: PaymentProviderValue | null): void {
    setPrefill(provider);
    setModalOpen(true);
  }

  function closeCreateModal(): void {
    setModalOpen(false);
    setPrefill(null);
  }

  return (
    <section className="flex flex-col gap-6" data-testid="payment-providers-page">
      <PageHeader
        title={t('payment_providers.title')}
        sub={t('payment_providers.subtitle')}
        actions={
          <Button
            variant="primary"
            onClick={() => openCreateModal(null)}
            data-testid="payment-providers-new"
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('payment_providers.new')}
          </Button>
        }
      />

      {/* Info banner — spells out the 60s cache contract so an operator
          who just rotated doesn't panic when the change isn't instant. */}
      <div
        className="flex items-start gap-3 rounded-lg border border-line-soft bg-card p-4"
        data-testid="payment-providers-info"
      >
        <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-signal" />
        <p className="m-0 text-body text-paper-mid">
          {t('payment_providers.banner')}
        </p>
      </div>

      {data.providers.length === 0 ? (
        <ProvidersEmptyState onCreate={() => openCreateModal(null)} />
      ) : (
        <div className="flex flex-col gap-6">
          {rowsByProvider.map(({ provider, rows }) => (
            <ProviderGroup
              key={provider}
              provider={provider}
              rows={rows}
              // DB `provider` column is free-form TEXT; guard before
              // handing it to the modal so an unknown provider string
              // (e.g. migration-ahead-of-enum) falls back to the blank-
              // provider path instead of corrupting the URL defaults.
              onRotate={() =>
                isPaymentProviderValue(provider)
                  ? openCreateModal(provider)
                  : openCreateModal(null)
              }
            />
          ))}
        </div>
      )}

      {isModalOpen ? (
        <CreateProviderModal
          data={data}
          prefillProvider={prefill}
          onClose={closeCreateModal}
        />
      ) : null}
    </section>
  );
}

// ─── Grouping helper ─────────────────────────────────────────────────────

interface ProviderGrouping {
  readonly provider: string;
  readonly rows: readonly PaymentProviderConfigRecord[];
}

function groupByProvider(
  records: readonly PaymentProviderConfigRecord[],
): readonly ProviderGrouping[] {
  const map = new Map<string, PaymentProviderConfigRecord[]>();
  for (const record of records) {
    const existing = map.get(record.provider);
    if (existing !== undefined) {
      existing.push(record);
    } else {
      map.set(record.provider, [record]);
    }
  }
  const out: ProviderGrouping[] = [];
  for (const [provider, rows] of map) {
    // Active first, then retired — the list response is already sorted
    // newest-first within that partition, so a stable partition suffices.
    const sorted = [...rows].sort((a, b) => {
      if (a.is_active === b.is_active) return 0;
      return a.is_active ? -1 : 1;
    });
    out.push({ provider, rows: sorted });
  }
  return out;
}

// ─── Provider group (one section per provider) ──────────────────────────

interface ProviderGroupProps {
  readonly provider: string;
  readonly rows: readonly PaymentProviderConfigRecord[];
  readonly onRotate: () => void;
}

function ProviderGroup({ provider, rows, onRotate }: ProviderGroupProps): JSX.Element {
  const t = useT();
  const hasActive = rows.some((r) => r.is_active);
  return (
    <Panel
      testId={`payment-providers-group-${provider}`}
      caption={
        <span className="inline-flex items-center gap-2">
          <CreditCard aria-hidden="true" className="h-4 w-4 text-signal" />
          <span className="font-mono">{provider}</span>
        </span>
      }
      actions={
        hasActive ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRotate}
            data-testid={`payment-providers-rotate-${provider}`}
          >
            <ArrowLeftRight aria-hidden="true" className="h-3.5 w-3.5" />
            {t('payment_providers.row.rotate')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-2 p-3">
        {rows.map((row) => (
          <ProviderRow key={row.id} row={row} />
        ))}
      </div>
    </Panel>
  );
}

// ─── One row — masked key + reveal + status chip ─────────────────────────

interface ProviderRowProps {
  readonly row: PaymentProviderConfigRecord;
}

function ProviderRow({ row }: ProviderRowProps): JSX.Element {
  const t = useT();
  const reveal = useRevealPaymentProviderKey();
  const toast = useToast();
  const [revealed, setRevealed] = useState<string | null>(null);

  async function handleReveal(): Promise<void> {
    try {
      const { secret_key } = await reveal.mutateAsync(row.id);
      setRevealed(secret_key);
    } catch (err) {
      toast.error(
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err),
      );
    }
  }

  return (
    <div
      data-testid={`payment-providers-row-${row.id}`}
      data-is-active={row.is_active}
      className={[
        'grid grid-cols-[minmax(0,1fr)_180px_120px_160px_140px] items-center gap-4 rounded-lg px-4 py-3 transition-colors duration-120',
        row.is_active ? 'bg-raised' : 'bg-well',
      ].join(' ')}
    >
      {/* Credentials (id + masked or revealed key) */}
      <div className="flex min-w-0 flex-col gap-1">
        <span
          className="truncate font-mono text-label text-paper-low"
          data-testid={`payment-providers-row-${row.id}-id`}
          title={row.id}
        >
          {row.id}
        </span>
        <span
          className="truncate font-mono text-body text-paper"
          data-testid={`payment-providers-row-${row.id}-key`}
        >
          {revealed !== null ? revealed : row.secret_key_masked}
        </span>
      </div>

      {/* PID + submit URL — stacked so the wider column doesn't overflow on small screens */}
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-label text-paper-low">
          {t('payment_providers.row.pid')}
        </span>
        <span
          className="truncate font-mono text-dense text-paper"
          data-testid={`payment-providers-row-${row.id}-pid`}
          title={row.pid}
        >
          {row.pid}
        </span>
      </div>

      {/* Status chip */}
      {row.is_active ? (
        <Chip tone="ok" testId="payment-providers-chip-active" className="w-fit">
          {t('payment_providers.chip.active')}
        </Chip>
      ) : (
        <Chip tone="neutral" testId="payment-providers-chip-retired" className="w-fit">
          {t('payment_providers.chip.retired')}
        </Chip>
      )}

      {/* Updated metadata */}
      <div className="flex min-w-0 flex-col gap-1">
        <span
          className="truncate text-dense text-paper-mid"
          data-testid={`payment-providers-row-${row.id}-updated-by`}
          title={row.updated_by ?? ''}
        >
          {row.updated_by ?? '—'}
        </span>
        <span className="font-mono text-label text-paper-low">
          {formatIso(row.updated_at)}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void handleReveal()}
          disabled={reveal.isPending}
          data-testid={`payment-providers-row-${row.id}-reveal`}
        >
          {revealed !== null ? (
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <Eye aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          {revealed !== null
            ? t('payment_providers.row.revealed')
            : reveal.isPending
              ? t('payment_providers.row.revealing')
              : t('payment_providers.row.reveal')}
        </Button>
      </div>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────

interface ProvidersEmptyStateProps {
  readonly onCreate: () => void;
}

function ProvidersEmptyState({ onCreate }: ProvidersEmptyStateProps): JSX.Element {
  const t = useT();
  return (
    <div className="rounded-lg border border-dashed border-line bg-card">
      <EmptyState
        testId="payment-providers-empty"
        icon={<Wallet aria-hidden="true" className="h-5 w-5" />}
        title={t('payment_providers.empty.title')}
        hint={t('payment_providers.empty.body')}
        action={
          <Button
            variant="primary"
            onClick={onCreate}
            data-testid="payment-providers-empty-create"
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('payment_providers.empty.cta')}
          </Button>
        }
      />
    </div>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────

interface CreateProviderModalProps {
  readonly data: ListPaymentProvidersResponse;
  readonly prefillProvider: PaymentProviderValue | null;
  readonly onClose: () => void;
}

interface DraftRow {
  readonly provider: PaymentProviderValue;
  readonly pid: string;
  readonly secretKey: string;
  readonly submitUrl: string;
  readonly mapiUrl: string;
  readonly queryUrl: string;
  readonly note: string;
  readonly makeActive: boolean;
}

function CreateProviderModal({
  data,
  prefillProvider,
  onClose,
}: CreateProviderModalProps): JSX.Element {
  const t = useT();
  // Prefill is either a "Rotate" click on an existing active row (we
  // seed the URLs from it to save typing) or a blank "+ New Provider"
  // click from the top-right button (blank form, zpay-default URLs).
  const prefillRow = useMemo(() => {
    if (prefillProvider === null) return null;
    return (
      data.providers.find(
        (r) => r.provider === prefillProvider && r.is_active,
      ) ?? null
    );
  }, [data.providers, prefillProvider]);

  const [provider, setProvider] = useState<PaymentProviderValue>(
    prefillProvider ?? 'zpay',
  );
  const [pid, setPid] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [submitUrl, setSubmitUrl] = useState(
    prefillRow?.submit_url ?? defaultSubmitUrl(provider),
  );
  const [mapiUrl, setMapiUrl] = useState(
    prefillRow?.mapi_url ?? defaultMapiUrl(provider),
  );
  const [queryUrl, setQueryUrl] = useState(
    prefillRow?.query_url ?? defaultQueryUrl(provider),
  );
  const [note, setNote] = useState('');
  // Default to true because the common workflow is "seed first row" /
  // "rotate" — both want the new row active. Operator can uncheck for
  // a draft-style staging write.
  const [makeActive, setMakeActive] = useState(true);

  const [formError, setFormError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftRow | null>(null);

  const hasPriorActive = useMemo(
    () => data.providers.some((r) => r.provider === provider && r.is_active),
    [data.providers, provider],
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const pidTrim = pid.trim();
    const submitTrim = submitUrl.trim();
    const mapiTrim = mapiUrl.trim();
    const queryTrim = queryUrl.trim();
    if (pidTrim.length === 0) {
      setFormError(t('payment_providers.create.error.pid_required'));
      return;
    }
    // secret_key is NOT trimmed on the server (some vendors issue keys
    // with leading/trailing whitespace that matter for signing). We
    // accept the value verbatim; the server rejects pure-whitespace.
    if (secretKey.length === 0 || secretKey.trim().length === 0) {
      setFormError(t('payment_providers.create.error.secret_required'));
      return;
    }
    if (submitTrim.length === 0) {
      setFormError(t('payment_providers.create.error.submit_url_required'));
      return;
    }
    if (queryTrim.length === 0) {
      setFormError(t('payment_providers.create.error.query_url_required'));
      return;
    }
    if (provider === 'zpay' && mapiTrim.length === 0) {
      setFormError(t('payment_providers.create.error.mapi_url_required_zpay'));
      return;
    }

    setDraft({
      provider,
      pid: pidTrim,
      secretKey,
      submitUrl: submitTrim,
      mapiUrl: mapiTrim,
      queryUrl: queryTrim,
      note: note.trim(),
      makeActive,
    });
  }

  function handleModalClose(): void {
    setDraft(null);
  }

  // When the user picks a different provider from the select, re-seed
  // the three URL fields with that provider's canonical defaults —
  // unless they've typed something other than the previous default
  // (then leave their edit alone).
  function handleProviderChange(next: PaymentProviderValue): void {
    setProvider(next);
    if (submitUrl === defaultSubmitUrl(provider)) {
      setSubmitUrl(defaultSubmitUrl(next));
    }
    if (mapiUrl === defaultMapiUrl(provider)) {
      setMapiUrl(defaultMapiUrl(next));
    }
    if (queryUrl === defaultQueryUrl(provider)) {
      setQueryUrl(defaultQueryUrl(next));
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="payment-providers-create-title"
      testId="payment-providers-create-modal"
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 p-6">
        <header className="flex items-center gap-3">
          <CreditCard aria-hidden="true" className="h-5 w-5 text-signal" />
          <h3
            id="payment-providers-create-title"
            className="m-0 text-title text-paper"
          >
            {hasPriorActive
              ? t('payment_providers.create.modal.title.rotate', { provider })
              : t('payment_providers.create.modal.title.new', { provider })}
          </h3>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label={t('payment_providers.create.provider')}
            fieldId="payment-providers-create-provider"
          >
            <Select
              value={provider}
              onValueChange={(value) => {
                // Options are rendered from PAYMENT_PROVIDER_VALUES, so
                // the value is always in range under normal UI use. Guard
                // anyway (no `as` escape hatches) — a browser extension
                // or e2e test injecting an invalid option would otherwise
                // silently widen `provider` to `string` and corrupt the
                // URL-defaults lookup below.
                if (isPaymentProviderValue(value)) {
                  handleProviderChange(value);
                }
              }}
              options={PAYMENT_PROVIDER_VALUES.map((v) => ({ value: v, label: v }))}
              ariaLabel={t('payment_providers.create.provider')}
              mono
              disabled={prefillProvider !== null}
              testId="payment-providers-create-provider"
            />
          </FormField>
          <FormField
            label={t('payment_providers.create.pid')}
            fieldId="payment-providers-create-pid"
          >
            <Input
              id="payment-providers-create-pid"
              type="text"
              mono
              value={pid}
              onChange={(e) => setPid(e.currentTarget.value)}
              autoComplete="off"
              data-testid="payment-providers-create-pid"
            />
          </FormField>
        </div>

        <FormField
          label={t('payment_providers.create.secret')}
          fieldId="payment-providers-create-key"
          hint={t('payment_providers.create.secret.hint')}
        >
          <Input
            id="payment-providers-create-key"
            type="password"
            mono
            value={secretKey}
            onChange={(e) => setSecretKey(e.currentTarget.value)}
            autoComplete="new-password"
            data-testid="payment-providers-create-key"
          />
        </FormField>

        <FormField
          label={t('payment_providers.create.submit_url')}
          fieldId="payment-providers-create-submit-url"
        >
          <Input
            id="payment-providers-create-submit-url"
            type="url"
            mono
            value={submitUrl}
            onChange={(e) => setSubmitUrl(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-submit-url"
          />
        </FormField>

        <FormField
          label={
            <>
              {t('payment_providers.create.mapi_url')}
              {provider !== 'zpay' ? (
                <span className="text-paper-low">
                  {' '}
                  {t('payment_providers.create.mapi_url.optional_suffix')}
                </span>
              ) : null}
            </>
          }
          fieldId="payment-providers-create-mapi-url"
        >
          <Input
            id="payment-providers-create-mapi-url"
            type="url"
            mono
            value={mapiUrl}
            onChange={(e) => setMapiUrl(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-mapi-url"
          />
        </FormField>

        <FormField
          label={t('payment_providers.create.query_url')}
          fieldId="payment-providers-create-query-url"
        >
          <Input
            id="payment-providers-create-query-url"
            type="url"
            mono
            value={queryUrl}
            onChange={(e) => setQueryUrl(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-query-url"
          />
        </FormField>

        <FormField
          label={t('payment_providers.create.note')}
          fieldId="payment-providers-create-note"
        >
          <Input
            id="payment-providers-create-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-note"
          />
        </FormField>

        <div className="flex items-start gap-3 rounded-lg bg-well p-3">
          <div className="mt-0.5">
            <Checkbox
              id="payment-providers-create-make-active"
              checked={makeActive}
              onCheckedChange={setMakeActive}
              testId="payment-providers-create-make-active"
            />
          </div>
          <label
            htmlFor="payment-providers-create-make-active"
            className="flex cursor-pointer flex-col gap-1"
          >
            <span className="text-body font-medium text-paper">
              {t('payment_providers.create.active_checkbox', { provider })}
            </span>
            <span
              className={[
                'text-label',
                hasPriorActive && makeActive ? 'text-warn' : 'text-paper-low',
              ].join(' ')}
            >
              {hasPriorActive && makeActive
                ? t('payment_providers.create.active_hint.rotation')
                : hasPriorActive
                  ? t('payment_providers.create.active_hint.retired')
                  : t('payment_providers.create.active_hint.first')}
            </span>
          </label>
        </div>

        {formError !== null ? (
          <p
            role="alert"
            className="m-0 text-label text-bad"
            data-testid="payment-providers-create-error"
          >
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="payment-providers-create-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            data-testid="payment-providers-create-submit"
          >
            {hasPriorActive && makeActive
              ? t('payment_providers.create.submit.continue')
              : t('payment_providers.create.submit.save')}
          </Button>
        </div>
      </form>

      {draft !== null ? (
        <ConfirmModal
          draft={draft}
          hasPriorActive={hasPriorActive}
          onClose={(success) => {
            handleModalClose();
            if (success) onClose();
          }}
        />
      ) : null}
    </Modal>
  );
}

// ─── Type-to-confirm modal ───────────────────────────────────────────────

interface ConfirmModalProps {
  readonly draft: DraftRow;
  readonly hasPriorActive: boolean;
  readonly onClose: (success: boolean) => void;
}

function ConfirmModal({ draft, hasPriorActive, onClose }: ConfirmModalProps): JSX.Element {
  const t = useT();
  const create = useCreatePaymentProvider();
  const toast = useToast();

  // Type-to-confirm is only required when make_active=true AND a prior
  // active row exists (i.e. this write will RETIRE another row). All
  // other writes go through with a single submit click.
  const requiresTypeConfirm = draft.makeActive && hasPriorActive;
  const [typed, setTyped] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const typedMatches = !requiresTypeConfirm || typed.trim() === draft.provider;

  useEffect(() => {
    setServerError(null);
  }, [typed]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!typedMatches) {
      setServerError(
        t('payment_providers.confirm.error.type_mismatch', { provider: draft.provider }),
      );
      return;
    }
    try {
      const body: CreatePaymentProviderRequest = {
        provider: draft.provider,
        pid: draft.pid,
        secret_key: draft.secretKey,
        submit_url: draft.submitUrl,
        mapi_url: draft.mapiUrl.length > 0 ? draft.mapiUrl : null,
        query_url: draft.queryUrl,
        make_active: draft.makeActive,
        note: draft.note.length > 0 ? draft.note : undefined,
      };
      const res = await create.mutateAsync(body);
      toast.success(
        res.retired !== null
          ? t('payment_providers.toast.rotated', {
              provider: draft.provider,
              retiredId: res.retired.id,
            })
          : t('payment_providers.toast.saved', {
              provider: draft.provider,
              status: res.created.is_active
                ? t('payment_providers.toast.status.active')
                : t('payment_providers.toast.status.retired'),
            }),
      );
      onClose(true);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setServerError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => onClose(false)}
      titleId="payment-providers-confirm-title"
      testId="payment-providers-confirm-modal"
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 p-6">
        <header className="flex flex-col gap-2">
          <h3
            id="payment-providers-confirm-title"
            className="m-0 text-title text-paper"
          >
            {requiresTypeConfirm
              ? t('payment_providers.confirm.title.rotation', { provider: draft.provider })
              : t('payment_providers.confirm.title.save', { provider: draft.provider })}
          </h3>
          <p className="m-0 text-body text-paper-mid">
            {requiresTypeConfirm
              ? t('payment_providers.confirm.body.rotation')
              : t('payment_providers.confirm.body.save')}
          </p>
        </header>

        <div className="flex flex-col gap-2 rounded-lg bg-well p-4 font-mono text-dense">
          <span>
            <span className="text-paper-low">provider:</span>{' '}
            <span className="text-signal">{draft.provider}</span>
          </span>
          <span>
            <span className="text-paper-low">pid:</span>{' '}
            <span className="text-paper">{draft.pid}</span>
          </span>
          <span>
            <span className="text-paper-low">key:</span>{' '}
            <span
              className="text-paper"
              data-testid="payment-providers-confirm-key-preview"
            >
              {maskKeyPreview(draft.secretKey)}
            </span>
          </span>
          <span>
            <span className="text-paper-low">make_active:</span>{' '}
            <span className={draft.makeActive ? 'text-signal' : 'text-paper-low'}>
              {draft.makeActive ? 'true' : 'false'}
            </span>
          </span>
        </div>

        {requiresTypeConfirm ? (
          <FormField
            label={
              <>
                {t('payment_providers.confirm.type_prefix')}
                <code className="font-mono text-signal">{draft.provider}</code>
                {t('payment_providers.confirm.type_suffix')}
              </>
            }
            fieldId="payment-providers-confirm-type"
          >
            <Input
              id="payment-providers-confirm-type"
              type="text"
              mono
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              autoComplete="off"
              data-testid="payment-providers-confirm-type"
            />
          </FormField>
        ) : null}

        {serverError !== null ? (
          <p
            role="alert"
            className="m-0 text-label text-bad"
            data-testid="payment-providers-confirm-error"
          >
            {serverError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onClose(false)}
            data-testid="payment-providers-confirm-cancel"
          >
            {t('payment_providers.confirm.back')}
          </Button>
          <Button
            type="submit"
            variant={requiresTypeConfirm ? 'danger' : 'primary'}
            disabled={!typedMatches}
            loading={create.isPending}
            data-testid="payment-providers-confirm-submit"
          >
            {create.isPending
              ? t('payment_providers.confirm.submit.saving')
              : requiresTypeConfirm
                ? t('payment_providers.confirm.submit.rotate')
                : t('payment_providers.confirm.submit.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatIso(iso: string): string {
  return formatLocalTimestamp(iso, { seconds: true });
}

/**
 * Render a key preview for the ConfirmModal diff pane. For keys longer
 * than 8 chars, show first 4 + bullets + last 4 (same mask language the
 * server uses in the list view). For keys ≤ 8 chars the head/tail
 * slices would OVERLAP and expose plaintext (e.g. a 4-char key would
 * render as `abcdabcd`); in that case render all bullets so the masked
 * affordance stays honest at every length. Mirrors the `maskSecretKey`
 * logic in `apps/platform-api/src/db/payment-provider-configs.ts`.
 */
export function maskKeyPreview(key: string): string {
  if (key.length <= 8) {
    return '•'.repeat(Math.max(1, key.length));
  }
  return `${key.slice(0, 4)}${'•'.repeat(key.length - 8)}${key.slice(-4)}`;
}

/**
 * Canonical URL defaults per provider — used to pre-populate the create
 * form. Operators can override if an aggregator routes them at regional
 * endpoints, but the defaults cover the common case.
 *
 * Modeled as `Record<PaymentProviderValue, string>` (not a switch) so
 * adding a new value to `PAYMENT_PROVIDER_VALUES` that isn't also added
 * to these tables is a compile error — no silent `undefined` returns
 * from exhaustive-looking but incomplete switches.
 *
 * Empty string means "no canonical default; the operator must type one"
 * — Stripe/WeChat/Alipay MAPI doesn't exist (different mode), and
 * WeChat/Alipay official APIs have tenant-specific endpoints.
 */
const DEFAULT_SUBMIT_URL: Record<PaymentProviderValue, string> = {
  zpay: 'https://zpayz.cn/submit.php',
  stripe: 'https://api.stripe.com/v1/checkout/sessions',
  wechat: '',
  alipay: '',
};

const DEFAULT_MAPI_URL: Record<PaymentProviderValue, string> = {
  zpay: 'https://zpayz.cn/mapi.php',
  stripe: '',
  wechat: '',
  alipay: '',
};

const DEFAULT_QUERY_URL: Record<PaymentProviderValue, string> = {
  zpay: 'https://zpayz.cn/api.php',
  stripe: 'https://api.stripe.com/v1/checkout/sessions',
  wechat: '',
  alipay: '',
};

function defaultSubmitUrl(provider: PaymentProviderValue): string {
  return DEFAULT_SUBMIT_URL[provider];
}

function defaultMapiUrl(provider: PaymentProviderValue): string {
  return DEFAULT_MAPI_URL[provider];
}

function defaultQueryUrl(provider: PaymentProviderValue): string {
  return DEFAULT_QUERY_URL[provider];
}
